import {Component, effect, inject, OnInit, Signal} from '@angular/core';
import {FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {Store} from '@ngrx/store';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {_DEFAULT_APPLICATION_TOOLTIPS} from '../../../core/models';
import {IAddItemRule, SettingsReferenceEnum} from '../../models';
import {selectSelectedSetting, selectSelectedStoreAddItemRule} from '../../store/setting.selectors';
import {IUser} from '../../../user-access/models';
import {selectUser} from '../../../user-access/user-store/user.selectors';
import {IStore} from '../../../../shared/shared-models/store/store';
import {selectSelectedStore} from '../../../user-access/user-stores-store/user-store.selectors';
import {getFieldErrorString} from '../../../../shared/utils/form/form.utils';
import {settingsSetSelected, settingStoreStockItemAddRule} from '../../store/setting.actions';
import {ISelectOption} from '../../../../shared/shared-models/custom-select/custom-select';
import {ISharedModalBasic} from '../../../../shared/shared-models/modals/shared-modal-basic';
import {IModalDismissResult} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {ICrudOptions, IUserAccessFeatureStockRules} from '../../../user-access/models/user-access-features/access';
import {
  selectUserAccessFeatureStockRulesForSelectedStore,
} from '../../../user-access/user-access-store/user-access.selectors';

@Component({
  selector: 'app-feature-stock-rule-add',
  templateUrl: './feature-stock-rule-add.component.html',
  styleUrl: './feature-stock-rule-add.component.scss',
  standalone: false,
})
export class FeatureStockRuleAddComponent implements OnInit {

  //#region INJECTORS
  private formBuilder = inject(FormBuilder);
  private _store = inject(Store);
  private modalUtils = inject(SharedModalUtils);
  //#endregion

  //#region PROPERTIES
  formAddItemRule: FormGroup;

  protected readonly icons = IconsEnum;
  protected readonly iconColours = IconColourEnum;
  protected readonly tooltips = _DEFAULT_APPLICATION_TOOLTIPS;

  private readonly _settingChanged: Signal<SettingsReferenceEnum> = this._store.selectSignal(selectSelectedSetting);
  private readonly _addItemRule: Signal<IAddItemRule> = this._store.selectSignal(selectSelectedStoreAddItemRule);
  private readonly _user: Signal<IUser> = this._store.selectSignal(selectUser);
  private readonly _selectedStore: Signal<IStore> = this._store.selectSignal(selectSelectedStore);
  private readonly _uaStockRules: Signal<IUserAccessFeatureStockRules> = this._store.selectSignal(selectUserAccessFeatureStockRulesForSelectedStore);

  //#endregion

  //#region INITIALIZATION
  constructor() {
    effect(() => {
      const userAccess = this._uaStockRules();
      const addItemRule = this._addItemRule();
      this.initializeForms(userAccess, addItemRule);
    });
    effect(() => {
      const ref = this._settingChanged();
      this._store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
    });
  }

  //#endregion

  //#region ACCESSOR - ADD ITEM RULE SETTINGS

  get hasAddItemRuleBeenEdited(): boolean {
    return this.formAddItemRule?.dirty ?? false;
  }

  get hasAddItemRuleErrors(): {
    errors: ValidationErrors; error: string; field: string;
  }[] {
    const errors: { errors: ValidationErrors; error: string; field: string; }[] = [];
    const digitsErrors = this.formAddItemRule?.controls['digits']?.errors;
    const prefixErrors = this.formAddItemRule?.controls['prefix']?.errors;
    const suffixErrors = this.formAddItemRule?.controls['suffix']?.errors;
    const customPrefixErrors = this.formAddItemRule?.controls['customPrefix']?.errors;
    const customSuffixErrors = this.formAddItemRule?.controls['customSuffix']?.errors;

    if (digitsErrors) {
      errors.push({
        errors: digitsErrors,
        error: getFieldErrorString(digitsErrors, 'digits'),
        field: 'digits',
      });
    }
    if (prefixErrors) {
      errors.push({
        errors: prefixErrors,
        error: getFieldErrorString(prefixErrors, 'prefix'),
        field: 'prefix',
      });
    }
    if (suffixErrors) {
      errors.push({
        errors: suffixErrors,
        error: getFieldErrorString(suffixErrors, 'suffix'),
        field: 'suffix',
      });
    }
    if (customPrefixErrors) {
      errors.push({
        errors: customPrefixErrors,
        error: getFieldErrorString(customPrefixErrors, 'customPrefix'),
        field: 'customPrefix',
      });
    }
    if (customSuffixErrors) {
      errors.push({
        errors: customSuffixErrors,
        error: getFieldErrorString(customSuffixErrors, 'customSuffix'),
        field: 'customSuffix',
      });
    }
    return errors;
  }

  get isAddItemRuleSubmitDisabled(): boolean {
    return (this.canAddItemRuleBeEdited === false) || (this.canAddItemRuleBeEdited === true && this.hasAddItemRuleBeenEdited === false) || (this.formAddItemRule?.invalid ?? true);
  }

  get canAddItemRuleBeEdited(): boolean {
    const access = this.stockManagerUserAccess?.add;
    return access?.canEdit ?? false;
  }

  get canAddItemRuleBeSubmitted(): boolean {
    const access = this.stockManagerUserAccess?.add;
    return access?.canSubmit ?? false;
  }

  get canAddItemRuleBeViewed(): boolean {
    const access = this.stockManagerUserAccess?.add;
    return access?.canView ?? false;
  }

  //#endregion

  //#region ACCESSORS FORM
  get isPrefixDisabled(): boolean {
    return this.formAddItemRule.controls['prefix'].value !== 'Custom';
  }

  get isSuffixDisabled(): boolean {
    return this.formAddItemRule.controls['suffix'].value !== 'Custom';
  }

  //#endregion

  //#region ACCESSOR - USER SETTINGS + USER ACCESS + STORE
  get selectedStore(): IStore {
    return this._selectedStore();
  }

  get userSettings(): IUser {
    return this._user();
  }

  get stockManagerUserAccess(): IUserAccessFeatureStockRules {
    return this._uaStockRules();
  }

  //#endregion

  //#region ACCESSOR - GLOBALS
  get haveAnyEditsBeenMade(): boolean {
    return (this.formAddItemRule?.dirty ?? false);
  }

  get addItemRule(): IAddItemRule {
    return this._addItemRule();
  }

  //#endregion

  //#region LIFECYCLE HOOKS
  ngOnInit(): void {
    this.formAddItemRule = this.formBuilder.group({});
  }

  //#endregion

  //#region FORM - CLICK HANDLERS
  onRevertForm(form: string): void {
    switch (form) {
      case 'all':
        this.revertAddItemRuleDetails();
        break;
      case 'addItemRule':
        this.revertAddItemRuleDetails();
        break;
    }
  }

  onSubmitForm(form: string): void {
    if (this.isAddItemRuleSubmitDisabled) {
      return;
    }

    let data: Partial<IAddItemRule> = {...this.addItemRule};
    switch (form) {
      case 'all':
        data = {
          ...data,
          ...this.generateAddItemRulePatch(data),
        };
        break;
      case 'addItemRule':
        if (!this.canAddItemRuleBeSubmitted) {
          return;
        }
        data = {...data, ...this.generateAddItemRulePatch(data)};
        break;
      default:
        return;
    }
    this._store.dispatch(settingStoreStockItemAddRule.setAddItemRules({rules: data as IAddItemRule}));
    this.formAddItemRule.markAsPristine();
  }

  //#endregion

  //#region FORM - BUILD FORMS FROM ACCESS

  private initializeForms(access: IUserAccessFeatureStockRules, rule: IAddItemRule): void {
    this.buildAddItemRuleForm(access?.add, rule);
  }

  private buildAddItemRuleForm(access: ICrudOptions, rule: IAddItemRule): void {
    if (access?.canView && this.formAddItemRule.pristine) {
      const {digits, prefix, suffix, manualCodeEntryDefault} = rule ?? {digits: 1, prefix: 'none', suffix: 'none'};
      const tempForm = this.formBuilder.group({});
      const nPrefix = this.normalizeData(prefix);
      const nSuffix = this.normalizeData(suffix);

      this.addFormControl(tempForm, 'prefix', nPrefix, false, [Validators.required]);
      this.addFormControl(tempForm, 'customPrefix', nPrefix === 'Custom' ? (prefix ?? '') : '', false, [Validators.maxLength(10)]);
      this.addFormControl(tempForm, 'suffix', nSuffix, false, [Validators.required]);
      this.addFormControl(tempForm, 'customSuffix', nSuffix === 'Custom' ? (suffix ?? '') : '', false, [Validators.maxLength(10)]);
      this.addFormControl(tempForm, 'digits', digits ?? 1, false, [Validators.required, Validators.min(1), Validators.max(10)]);
      this.addFormControl(tempForm, 'manualCodeEntryDefault', manualCodeEntryDefault ?? false, false);

      tempForm.markAsPristine();
      this.formAddItemRule = tempForm;
    }
  }

  private addFormControl<T = unknown>(form: FormGroup, controlName: string, value: T, disabled: boolean, validators: ValidatorFn[] = []): void {
    form.addControl(controlName, this.formBuilder.control({value, disabled}, validators));
  }

  private normalizeData(key: string): string {
    if (!key || key === '') {
      return 'None';
    }
    const data: Record<string, string> = {
      dep: 'Department',
      Department: 'Department',
      subDep: 'Sub-department',
      ['Sub-department']: 'Sub-department',
      none: 'None',
      None: 'None',
    };
    return data[key] ?? 'Custom';
  }

  //#endregion

  //#region FORM - ON CLICK

  onSelectTokens(selected: { [key: string]: ISelectOption }, property: string): void {
    const selection = Object.keys(selected)
      .filter((option: string) => selected[option].selected);
    const selectedOpt: string = selection?.length > 0 ? selection[0] : null;
    this.formAddItemRule.controls[property].setValue(selectedOpt);
    this.formAddItemRule.markAsDirty();
  }

  /**
   * Toggles whether Add Stock Item opens with manual stock code entry enabled for this store.
   */
  onToggleManualCodeEntryDefault(toggleValue: boolean): void {
    const control = this.formAddItemRule?.controls['manualCodeEntryDefault'];
    if (!control || control.value === toggleValue) {
      return;
    }
    control.setValue(toggleValue);
    this.formAddItemRule.markAsDirty();
  }

  //#endregion

  //#region FORM - REVERT FORM EDITS
  private revertAddItemRuleDetails(): void {
    const rule = this.addItemRule;
    const {digits, prefix, suffix, manualCodeEntryDefault} = rule ?? {digits: 1, prefix: 'none', suffix: 'none'};
    const nPrefix = this.normalizeData(prefix);
    const nSuffix = this.normalizeData(suffix);

    this.formAddItemRule.reset({
      prefix: nPrefix,
      customPrefix: nPrefix === 'Custom' ? (prefix ?? '') : '',
      suffix: nSuffix,
      customSuffix: nSuffix === 'Custom' ? (suffix ?? '') : '',
      digits: digits ?? 1,
      manualCodeEntryDefault: manualCodeEntryDefault ?? false,
    });
  }

  //#endregion

  //#region FORM - GENERATE PATCH DATA

  private determineValue(fix: string, customValue?: string): string {
    let fixed = 'none';
    switch (fix) {
      case 'Department':
      case 'dep':
        fixed = 'dep';
        break;
      case 'Sub-department':
      case 'subDep':
        fixed = 'subDep';
        break;
      case 'None':
      case 'none':
        fixed = 'none';
        break;
      case 'Custom':
      case 'custom':
        fixed = customValue || 'gallix';
        break;
    }
    return fixed;
  }

  private generateAddItemRulePatch(data: Partial<IAddItemRule>): Partial<IAddItemRule> {
    const formValues = this.formAddItemRule.getRawValue();
    const currentDigits = this.addItemRule?.digits;
    const currentPrefix = this.addItemRule?.prefix?.trim() ?? '';
    const currentSuffix = this.addItemRule?.suffix?.trim() ?? '';

    const currentManualCodeEntryDefault = this.addItemRule?.manualCodeEntryDefault ?? false;

    const nextDigits = Number(formValues?.digits);
    const formPrefix = formValues?.prefix?.trim();
    const formSuffix = formValues?.suffix?.trim();
    const prefix: string = this.determineValue(formPrefix, formValues?.customPrefix?.trim());
    const suffix: string = this.determineValue(formSuffix, formValues?.customSuffix?.trim());
    const manualCodeEntryDefault = !!formValues?.manualCodeEntryDefault;

    const rulePatch: Partial<IAddItemRule> = {
      ...(currentDigits !== nextDigits && Number.isFinite(nextDigits) && {digits: nextDigits}),
      ...(currentPrefix !== prefix && {prefix}),
      ...(currentSuffix !== suffix && {suffix}),
      ...(currentManualCodeEntryDefault !== manualCodeEntryDefault && {manualCodeEntryDefault}),
    };

    if (!(Object.keys(rulePatch).length > 0)) {
      return data;
    }
    return {...data, ...rulePatch};
  }

  //#endregion

  //#region SELECT - OPTIONS

  getPrefixSuffixOptions(selectedValue: string | null | undefined): Record<string, ISelectOption> {
    const options = ['None', 'Department', 'Sub-department', 'Custom'];
    const selectOptions: Record<string, ISelectOption> = {};
    options.forEach((option: string) => {
      selectOptions[option] = {
        name: option,
        selected: option === selectedValue,
        disabled: false,
      };
    });
    return selectOptions;
  }

  //#endregion

  //#region MODAL - DISCARD UNSAVED CHANGES
  private async onShowModalUnsavedChanges(ref: SettingsReferenceEnum): Promise<void> {
    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Discard Changes & Continue',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Unsaved Changes',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextHeading: 'If you leave this page, your changes will be lost.',
      contextDescription: ['Are you sure you want to continue?'],
      cssClass: ['shared-basic-modal-css-small'],
    };
    await this.modalUtils.createModal(modalProps).then((result: IModalDismissResult) => {
      this._store.dispatch(settingsSetSelected.setSelectedReference({ref: SettingsReferenceEnum.accountUserProfile}));
      if (result.role === 'modal-accept') {
        this._store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
      }
    });
  }

//#endregion

}
